import React, { useRef, useState, useEffect } from 'react';
import { Drawer, Button, Spin, Tooltip, message, Dropdown, Modal, Tag } from 'antd';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import Prescriptions from 'components/Prescriptions';
import ChatBox from 'components/ChatBox';
import s from './s.module.less';
import useTreatmentAppointmentDetail from 'hooks/treatment/useTreatmentAppointmentDetail';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import { submitVideoTreatment } from 'api/treatment';
import { IVideoTreatment, EBusinessType } from 'types/treatment';
import { useExportPdf } from 'hooks/useExportPdf';
import PrintContent from './components/PrintContent';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { TextVisitAttachment } from 'types/textVisit';
import PreviewModal from './components/PreviewModal';
import { useReactToPrint } from 'react-to-print';
import ProviderSignature from 'components/ProviderSignature';
import dayjs from 'utils/dayjs';

interface VideoVisitUnSubmittedDrawerProps {
    show: boolean;
    onClose: () => void;
    onSave?: () => void;
    appointmentId?: number;
    // 只读模式：group practice 下从 prospects-detail 打开时，整个 drawer 不允许写操作
    readOnly?: boolean;
}

const VideoVisitUnSubmittedDrawer = ({ show, onClose, appointmentId, onSave, readOnly }: VideoVisitUnSubmittedDrawerProps) => {
    const [caseData, loading, fetchData] = useTreatmentAppointmentDetail(appointmentId);
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [currentPlan, setCurrentPlan] = useState<IVideoTreatment>();
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const prescriptionsRef = useRef<{ getValues: () => any }>(null);
    const previewContentRef = useRef<HTMLDivElement>(null);
    const downloadPdf = useExportPdf(previewContentRef);
    const [hasSignature, setHasSignature] = useState(false);
    const disableWrite = !!readOnly;
    const isUpcoming = caseData?.startAt ? dayjs(caseData.startAt).isAfter(dayjs()) : false;
    const disableSubmit = disableWrite || isUpcoming;
    useEffect(() => {
        if (show && appointmentId) {
            fetchData(appointmentId);
        }
    }, [show]);

    const handlePrint = useReactToPrint({
        contentRef: previewContentRef,
        documentTitle: 'Visit Notes',
    });

    // const handleDownload = () => {
    //     const plan = prescriptionsRef.current?.getValues();
    //     getPreviewPlan(plan);

    //     setTimeout(() => {
    //         downloadPdf('Treatment Plan');
    //     }, 0);
    // };

    const handleSubmitOrSave = async (isSave: boolean) => {
        if (!caseData) {
            return;
        }
        try {
            const params = prescriptionsRef.current?.getValues();
            if (!params.treatmentPlan) {
                message.error('Please add a visit notes');
                return;
            }
            const res = await submitVideoTreatment({
                ...params,
                attachments: params.attachments.map((file: TextVisitAttachment) => ({
                    name: file.name,
                    path: file.path,
                    type: file.type,
                })),
                businessType: EBusinessType.APPOINTMENT,
                businessId: appointmentId,
                submit: !isSave,
            });
            if (!res.error) {
                message.success(isSave ? 'Draft saved successfully.' : 'Visit Notes sent successfully.');
                setShowSubmitModal(false);
                onSave?.();
                onClose();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getPreviewPlan = async (plan: IVideoTreatment) => {
        let attachmentList = plan?.attachments || [];
        if (attachmentList.length !== 0 && attachmentList.some((attachment) => attachment.previewUrl)) {
            setCurrentPlan(plan);
            return;
        }
        // 预览草稿时没有 previewUrl
        try {
            attachmentList = await Promise.all(
                attachmentList.map(async (attachment) => {
                    const res = await providerGetKlarityResourcesUrl(attachment.path || '');
                    if (!res.error) {
                        return { ...attachment, previewUrl: res.data?.data?.singedUrl || '' };
                    }
                    return attachment;
                }),
            );
            setCurrentPlan({
                ...plan,
                attachments: attachmentList,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handlePreviewAsPatient = () => {
        const plan = prescriptionsRef.current?.getValues();
        getPreviewPlan(plan);
        setShowPreviewModal(true);
    };

    const moreMenuItems: MenuProps['items'] = [
        {
            key: 'preview',
            label: 'Preview as patient',
            onClick: handlePreviewAsPatient,
        },
        {
            key: 'print',
            label: (
                <Tooltip
                    title="Printing only includes the text of the visit notes. Attachments must be downloaded individually."
                    placement="right"
                >
                    <div className="w-full">Print</div>
                </Tooltip>
            ),
            onClick: () => {
                const plan = prescriptionsRef.current?.getValues();
                setCurrentPlan(plan);
                setTimeout(() => {
                    handlePrint();
                }, 0);
            },
        },
        // {
        //     key: 'download',
        //     label: (
        //         <Tooltip
        //             title="Printing only includes the text of the visit notes. Attachments must be downloaded individually."
        //             placement="right"
        //         >
        //             <div className='w-full'>Download</div>
        //         </Tooltip>
        //     ),
        //     onClick: handleDownload,
        // },
    ];

    const title = ['Video visit', caseData?.serviceSpecialty, caseData?.serviceType].filter(Boolean).join(': ');

    return (
        <>
            <Drawer
                open={show}
                width="99vw"
                onClose={onClose}
                placement="right"
                // zIndex={1004}
                rootClassName={s.drawer}
                title={
                    <div className={s.header}>
                        <span className="text-base font-medium">{title}</span>
                        <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={s.closeBtn} />
                    </div>
                }
                closeIcon={null}
                footer={
                    !loading && caseData ? (
                        <div className={s.footer}>
                            <Tooltip
                                title={isUpcoming ? 'You can submit this visit note once the visit starts. Save it as a draft for now.' : ''}
                                placement="top"
                                color="#FFFFFF"
                                overlayInnerStyle={{ padding: '12px', color: '#15191E' }}
                            >
                                <Button type="primary" onClick={() => setShowSubmitModal(true)} disabled={disableSubmit}>
                                    Submit
                                </Button>
                            </Tooltip>
                            <Button
                                onClick={() => handleSubmitOrSave(true)}
                                className={s.textBtn}
                                disabled={disableWrite}
                            >
                                Save draft
                            </Button>
                            <Dropdown
                                menu={{ items: moreMenuItems, selectable: true, defaultSelectedKeys: ['preview'] }}
                                placement="topLeft"
                                trigger={['click']}
                            >
                                <Button className={s.textBtn}>
                                    More <DownOutlined />
                                </Button>
                            </Dropdown>
                        </div>
                    ) : null
                }
                destroyOnClose
            >
                <div className={s.loadingWrapper}>
                    <Spin spinning={loading} tip="Loading...">
                        {caseData ? (
                            <div className={s.content}>
                                <div style={{ overflowY: 'auto', maxWidth: '30%' }}>
                                    <div className={s.previewDetail}>
                                        <ProsepectInfo
                                            useForModule
                                            hideCharge
                                            smallSize
                                            data={caseData.prospect}
                                            onUpdate={() => {
                                                fetchData(appointmentId!);
                                            }}
                                            videoVisitItem={caseData}
                                            onChatWithPatient={() => setIsChatOpen(true)}
                                            showBillingForVisit
                                        />
                                    </div>
                                </div>
                                <div className={s.prescriptions}>
                                    <Prescriptions
                                        ref={prescriptionsRef}
                                        businessId={caseData.id}
                                        caseData={caseData}
                                        refetch={(appointmentId) => fetchData(appointmentId as number)}
                                        type="video"
                                        showUpcomingVisits
                                    />
                                </div>
                                {isChatOpen && (
                                    <div className={s.chatBox}>
                                        <ChatBox patientId={caseData.clientId} onClose={() => setIsChatOpen(false)} />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ minHeight: 'calc(100vh - 194px)' }} />
                        )}
                    </Spin>
                </div>
            </Drawer>
            {caseData && (
                <div className={s.printWrapper}>
                    <PrintContent ref={previewContentRef} data={currentPlan} caseData={caseData} />
                </div>
            )}
            <Modal
                open={showSubmitModal}
                onCancel={() => setShowSubmitModal(false)}
                okText="Sign & Send"
                onOk={() => {
                    if (!hasSignature) {
                        message.warning('Please add your signature before submitting.');
                        return;
                    }
                    handleSubmitOrSave(false);
                }}
                width={512}
            >
                <div className="flex gap-4">
                    <div className={s.submitModalIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M8.68387 13.3419C8.88616 12.9381 9 12.4824 9 12C9 11.5176 8.88616 11.0619 8.68387 10.6581M8.68387 13.3419C8.19134 14.3251 7.17449 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.17449 9 8.19134 9.67492 8.68387 10.6581M8.68387 13.3419L15.3161 16.6581M8.68387 10.6581L15.3161 7.34193M15.3161 7.34193C15.8087 8.32508 16.8255 9 18 9C19.6569 9 21 7.65685 21 6C21 4.34315 19.6569 3 18 3C16.3431 3 15 4.34315 15 6C15 6.48237 15.1138 6.93815 15.3161 7.34193ZM15.3161 16.6581C15.1138 17.0619 15 17.5176 15 18C15 19.6569 16.3431 21 18 21C19.6569 21 21 19.6569 21 18C21 16.3431 19.6569 15 18 15C16.8255 15 15.8087 15.6749 15.3161 16.6581Z"
                                stroke="#0284C7"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="text-gray-900 text-lg font-medium">Submit visit notes to patient</div>
                        <div className="text-gray-500 text-sm">
                            Please review before submitting. After submission, the visit notes will be{' '}
                            <span className="font-bold">signed and sent to the patient immediately</span>. It will also
                            be recorded under your name with a timestamp.
                        </div>
                    </div>
                </div>
                <ProviderSignature onSignatureStatusChange={setHasSignature} />
            </Modal>
            {caseData && (
                <PreviewModal
                    showPreviewModal={showPreviewModal}
                    onClose={() => setShowPreviewModal(false)}
                    currentPlan={currentPlan!}
                    caseData={caseData}
                />
            )}
        </>
    );
};

export default VideoVisitUnSubmittedDrawer;
